import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { StockIssueRequestStore } from "../../../stores/StockIssueRequestStore";
import { StockIssueRequest } from "../../../../../models/StockIssueRequest/StockIssueRequest";

interface OrderStockIssueRequestsPanelProps {
  orderId: number;
  canView: boolean;
  refreshKey?: number;
}

const processStatusColor = (
  status?: string,
): "default" | "warning" | "success" | "error" | "info" => {
  switch (status) {
    case "return_pending":
      return "warning";
    case "returned":
      return "success";
    case "rejected":
    case "cancelled":
    case "shipment_cancelled":
      return "error";
    case "issued":
      return "info";
    default:
      return "default";
  }
};

const OrderStockIssueRequestsPanel: React.FC<OrderStockIssueRequestsPanelProps> =
  observer(({ orderId, canView, refreshKey = 0 }) => {
    const theme = useTheme();
    const store = useMemo(() => new StockIssueRequestStore(), []);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (!canView || !orderId) return;

      setLoading(true);
      store
        .getStockIssueRequests({
          order_id: String(orderId),
          page_no: 1,
          limit: 50,
          sort_by: "created_date",
          sort_order: "desc",
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, [canView, orderId, store, refreshKey]);

    const requests = store.stockIssueRequests;

    if (!canView) return null;

    return (
      <Card
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.customColors.lightGray}`,
          boxShadow: `0 2px 8px ${theme.palette.customColors.shadowLightBorder}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 4,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid #F1F5F9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Stock Issue Requests
          </CustomTypography>
          <MuiLink
            component={RouterLink}
            to={`/orders/stock-issue-request?order_id=${orderId}`}
            underline="hover"
            sx={{ fontSize: "0.875rem", fontWeight: 600 }}
          >
            View all SIRs for this order
          </MuiLink>
        </Box>

        <Box sx={{ px: 4, py: 2.5 }}>
          {loading ? (
            <CustomTypography variant="body2" color="text.secondary">
              Loading stock issue requests…
            </CustomTypography>
          ) : requests.length === 0 ? (
            <CustomTypography variant="body2" color="text.secondary">
              No stock issue requests raised for this order yet.
            </CustomTypography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    SIR #
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Process status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Products
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Shipment
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Created
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Created by
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((req: StockIssueRequest) => (
                  <TableRow key={req.id} hover>
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      <MuiLink
                        component={RouterLink}
                        to={`/orders/stock-issue-request/${req.id}`}
                        underline="hover"
                        fontWeight={700}
                      >
                        #{req.id}
                      </MuiLink>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          req.process_status_label || req.sir_status || "—"
                        }
                        color={processStatusColor(req.process_status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      {req.products?.length ?? 0}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      {req.shipment_id ? (
                        <MuiLink
                          component={RouterLink}
                          to={`/shipments?search_text_value=${req.shipment_id}`}
                          underline="hover"
                        >
                          #{req.shipment_id}
                        </MuiLink>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      {req.created_date ? formatDate(req.created_date) : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8125rem" }}>
                      {req.created_by_admin_name || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Card>
    );
  });

export default OrderStockIssueRequestsPanel;
